import { queryOptions } from "@tanstack/react-query";

import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import {
  type CalKillerFilters,
  type CalKillerResponse,
  isCalKillerResponse,
} from "./calKillerTypes";

export const calKillerQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards", "cal-killer"] as const,
  snapshot: (filters: CalKillerFilters) => [...calKillerQueryKeys.all, filters] as const,
};

async function fetchCalKillerSnapshot(filters: CalKillerFilters): Promise<CalKillerResponse> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  const query = params.size > 0 ? `?${params}` : "";
  const response = await fetch(`${getOperatorsHttpOrigin()}/api/v1/dashboards/cal-killer${query}`, {
    credentials: "include",
  });
  if (response.status === 401) throwOperatorsAuthExpired();
  if (response.status === 403) {
    const message = await readOperatorsAuthErrorMessage(response).catch(
      () => "You do not have access to Dashboards.",
    );
    throw new OperatorsForbiddenError(message || "You do not have access to Dashboards.");
  }
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load Cal Killer dashboard",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isCalKillerResponse(body)) {
    throw new Error("Cal Killer response was malformed");
  }
  return body;
}

export function calKillerSnapshotQueryOptions(filters: CalKillerFilters = {}) {
  return queryOptions({
    queryKey: calKillerQueryKeys.snapshot(filters),
    queryFn: () => fetchCalKillerSnapshot(filters),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export { fetchCalKillerSnapshot };
